// src/services/LocalStorageDataService.ts
import { Competition, Solution, User, Tag, DataDomain } from '../models';
import { 
  DataService, 
  CompetitionSearchQuery, 
  SolutionSearchQuery, 
  SimilarityType,
  DataUpdate 
} from './DataService';

/**
 * ローカルストレージを使用したデータサービスの実装
 * CSVデータをJSONとしてローカルストレージに保存し、検索や類似性検索を提供
 */
export class LocalStorageDataService implements DataService {
  private readonly COMPETITIONS_KEY = 'kaggle_competitions';
  private readonly SOLUTIONS_KEY = 'kaggle_solutions';
  private readonly USERS_KEY = 'kaggle_users';
  private readonly TAGS_KEY = 'kaggle_tags';
  private readonly DATA_DOMAINS_KEY = 'kaggle_data_domains';
  private readonly SCHEMA_VERSION_KEY = 'kaggle_schema_version';
  
  private currentSchemaVersion = '1.0.0';

  constructor() {
    this.initializeStorage();
  }

  /**
   * ストレージの初期化とスキーマバージョンの確認
   */
  private initializeStorage(): void {
    // スキーマバージョンの確認
    const storedVersion = localStorage.getItem(this.SCHEMA_VERSION_KEY);
    
    if (!storedVersion) {
      // 初回実行時
      localStorage.setItem(this.SCHEMA_VERSION_KEY, this.currentSchemaVersion);
      this.initializeEmptyCollections();
    } else if (storedVersion !== this.currentSchemaVersion) {
      // スキーマバージョンが異なる場合はマイグレーションを実行
      this.migrateData(storedVersion, this.currentSchemaVersion);
    }
  }

  /**
   * 空のコレクションを初期化
   */
  private initializeEmptyCollections(): void {
    localStorage.setItem(this.COMPETITIONS_KEY, JSON.stringify([]));
    localStorage.setItem(this.SOLUTIONS_KEY, JSON.stringify([]));
    localStorage.setItem(this.USERS_KEY, JSON.stringify([]));
    localStorage.setItem(this.TAGS_KEY, JSON.stringify([]));
    localStorage.setItem(this.DATA_DOMAINS_KEY, JSON.stringify([]));
  }

  /**
   * データマイグレーションの実行
   * @param fromVersion 現在のバージョン
   * @param toVersion 新しいバージョン
   */
  private migrateData(fromVersion: string, toVersion: string): void {
    console.log(`Migrating data from version ${fromVersion} to ${toVersion}`);
    
    // 実際のマイグレーションロジックはここに実装
    // 例: 新しいフィールドの追加、データ構造の変更など
    
    // マイグレーション完了後、新しいバージョンを保存
    localStorage.setItem(this.SCHEMA_VERSION_KEY, toVersion);
  }

  // DataService インターフェースの実装

  async getCompetitions(): Promise<Competition[]> {
    const data = localStorage.getItem(this.COMPETITIONS_KEY);
    return data ? JSON.parse(data) : [];
  }

  async getCompetitionBySlug(slug: string): Promise<Competition | null> {
    const competitions = await this.getCompetitions();
    return competitions.find(comp => comp.slug === slug) || null;
  }

  async searchCompetitions(query: CompetitionSearchQuery): Promise<Competition[]> {
    let competitions = await this.getCompetitions();
    
    // テキスト検索
    if (query.text) {
      const searchText = query.text.toLowerCase();
      competitions = competitions.filter(comp => 
        comp.title.toLowerCase().includes(searchText) || 
        comp.subtitle.toLowerCase().includes(searchText) ||
        (comp.description && comp.description.toLowerCase().includes(searchText))
      );
    }
    
    // タグによるフィルタリング
    if (query.tags && query.tags.length > 0) {
      competitions = competitions.filter(comp => 
        query.tags!.every(tag => comp.tags.includes(tag))
      );
    }
    
    // データドメインによるフィルタリング
    if (query.dataDomain) {
      competitions = competitions.filter(comp => 
        comp.dataDomain === query.dataDomain
      );
    }
    
    // 日付範囲によるフィルタリング
    if (query.startDate) {
      const startDate = new Date(query.startDate);
      competitions = competitions.filter(comp => 
        new Date(comp.deadlineDate) >= startDate
      );
    }
    
    if (query.endDate) {
      const endDate = new Date(query.endDate);
      competitions = competitions.filter(comp => 
        new Date(comp.deadlineDate) <= endDate
      );
    }
    
    // 参加者数によるフィルタリング
    if (query.minParticipants !== undefined) {
      competitions = competitions.filter(comp => 
        comp.participantsCount >= query.minParticipants!
      );
    }
    
    if (query.maxParticipants !== undefined) {
      competitions = competitions.filter(comp => 
        comp.participantsCount <= query.maxParticipants!
      );
    }
    
    // アクティブ状態によるフィルタリング
    if (query.isActive !== undefined) {
      competitions = competitions.filter(comp => 
        comp.isActive === query.isActive
      );
    }
    
    return competitions;
  }

  async findSimilarCompetitions(
    slug: string, 
    similarityType: SimilarityType, 
    limit: number = 10
  ): Promise<Competition[]> {
    const targetComp = await this.getCompetitionBySlug(slug);
    if (!targetComp) {
      return [];
    }
    
    const competitions = await this.getCompetitions();
    let similarComps: Array<{competition: Competition, score: number}> = [];
    
    switch (similarityType) {
      case SimilarityType.TITLE:
        // タイトルの類似性
        similarComps = competitions
          .filter(comp => comp.slug !== slug)
          .map(comp => ({
            competition: comp,
            score: this.calculateTextSimilarity(targetComp.title, comp.title)
          }));
        break;
        
      case SimilarityType.TAGS:
        // タグの類似性
        similarComps = competitions
          .filter(comp => comp.slug !== slug)
          .map(comp => ({
            competition: comp,
            score: this.calculateTagSimilarity(targetComp.tags, comp.tags)
          }));
        break;
        
      case SimilarityType.DATA_DOMAIN:
        // データドメインの類似性
        similarComps = competitions
          .filter(comp => comp.slug !== slug)
          .map(comp => ({
            competition: comp,
            score: targetComp.dataDomain === comp.dataDomain ? 1 : 0
          }));
        break;
        
      case SimilarityType.DESCRIPTION:
        // 説明文の類似性
        similarComps = competitions
          .filter(comp => comp.slug !== slug && comp.description && targetComp.description)
          .map(comp => ({
            competition: comp,
            score: this.calculateTextSimilarity(
              targetComp.description || '', 
              comp.description || ''
            )
          }));
        break;
        
      case SimilarityType.ALL:
      default:
        // 総合的な類似性
        similarComps = competitions
          .filter(comp => comp.slug !== slug)
          .map(comp => ({
            competition: comp,
            score: (
              this.calculateTextSimilarity(targetComp.title, comp.title) * 0.3 +
              this.calculateTagSimilarity(targetComp.tags, comp.tags) * 0.3 +
              (targetComp.dataDomain === comp.dataDomain ? 0.2 : 0) +
              this.calculateTextSimilarity(
                targetComp.description || '', 
                comp.description || ''
              ) * 0.2
            )
          }));
        break;
    }
    
    // スコアでソートして上位のみ返す
    return similarComps
      .sort((a, b) => b.score - a.score)
      .slice(0, limit)
      .map(item => item.competition);
  }

  async getSolutions(): Promise<Solution[]> {
    const data = localStorage.getItem(this.SOLUTIONS_KEY);
    return data ? JSON.parse(data) : [];
  }

  async getSolutionsByCompetition(competitionSlug: string): Promise<Solution[]> {
    const solutions = await this.getSolutions();
    return solutions.filter(sol => sol.competitionSlug === competitionSlug);
  }

  async searchSolutions(query: SolutionSearchQuery): Promise<Solution[]> {
    let solutions = await this.getSolutions();
    
    // コンペティションによるフィルタリング
    if (query.competitionSlug) {
      solutions = solutions.filter(sol => 
        sol.competitionSlug === query.competitionSlug
      );
    }
    
    // ランクによるフィルタリング
    if (query.minRank !== undefined) {
      solutions = solutions.filter(sol => sol.rank >= query.minRank!);
    }
    
    if (query.maxRank !== undefined) {
      solutions = solutions.filter(sol => sol.rank <= query.maxRank!);
    }
    
    // スコアによるフィルタリング
    if (query.minScore !== undefined) {
      solutions = solutions.filter(sol => sol.score >= query.minScore!);
    }
    
    if (query.maxScore !== undefined) {
      solutions = solutions.filter(sol => sol.score <= query.maxScore!);
    }
    
    // ユーザーIDによるフィルタリング
    if (query.userId) {
      solutions = solutions.filter(sol => 
        sol.userIds.includes(query.userId!)
      );
    }
    
    // テキスト検索
    if (query.text) {
      const searchText = query.text.toLowerCase();
      solutions = solutions.filter(sol => 
        sol.summary.toLowerCase().includes(searchText)
      );
    }
    
    return solutions;
  }

  async getUsers(): Promise<User[]> {
    const data = localStorage.getItem(this.USERS_KEY);
    return data ? JSON.parse(data) : [];
  }

  async getUserById(userId: string): Promise<User | null> {
    const users = await this.getUsers();
    return users.find(user => user.userId === userId) || null;
  }

  async getTags(): Promise<Tag[]> {
    const data = localStorage.getItem(this.TAGS_KEY);
    return data ? JSON.parse(data) : [];
  }

  async getDataDomains(): Promise<DataDomain[]> {
    const data = localStorage.getItem(this.DATA_DOMAINS_KEY);
    return data ? JSON.parse(data) : [];
  }

  async updateData(data: DataUpdate): Promise<void> {
    if (data.competitions) {
      await this.updateCompetitions(data.competitions);
    }
    
    if (data.solutions) {
      await this.updateSolutions(data.solutions);
    }
    
    if (data.users) {
      await this.updateUsers(data.users);
    }
    
    if (data.tags) {
      await this.updateTags(data.tags);
    }
    
    if (data.dataDomains) {
      await this.updateDataDomains(data.dataDomains);
    }
  }

  // ヘルパーメソッド - マイグレーションサービスからアクセスできるようにpublicに変更

  /**
   * コンペティションデータの更新
   */
  async updateCompetitions(competitions: Competition[]): Promise<void> {
    localStorage.setItem(this.COMPETITIONS_KEY, JSON.stringify(competitions));
  }

  /**
   * ソリューションデータの更新
   */
  async updateSolutions(solutions: Solution[]): Promise<void> {
    localStorage.setItem(this.SOLUTIONS_KEY, JSON.stringify(solutions));
  }

  /**
   * ユーザーデータの更新
   */
  async updateUsers(users: User[]): Promise<void> {
    localStorage.setItem(this.USERS_KEY, JSON.stringify(users));
  }

  /**
   * タグデータの更新
   */
  async updateTags(tags: Tag[]): Promise<void> {
    localStorage.setItem(this.TAGS_KEY, JSON.stringify(tags));
  }

  /**
   * データドメインの更新
   */
  async updateDataDomains(dataDomains: DataDomain[]): Promise<void> {
    localStorage.setItem(this.DATA_DOMAINS_KEY, JSON.stringify(dataDomains));
  }

  /**
   * テキストの類似性を計算（簡易的なJaccard類似度）
   */
  private calculateTextSimilarity(text1: string, text2: string): number {
    if (!text1 || !text2) return 0;
    
    const words1 = new Set(text1.toLowerCase().split(/\s+/));
    const words2 = new Set(text2.toLowerCase().split(/\s+/));
    
    const intersection = new Set([...words1].filter(word => words2.has(word)));
    const union = new Set([...words1, ...words2]);
    
    return intersection.size / union.size;
  }

  /**
   * タグの類似性を計算
   */
  private calculateTagSimilarity(tags1: string[], tags2: string[]): number {
    if (!tags1.length || !tags2.length) return 0;
    
    const set1 = new Set(tags1);
    const set2 = new Set(tags2);
    
    const intersection = new Set([...set1].filter(tag => set2.has(tag)));
    const union = new Set([...set1, ...set2]);
    
    return intersection.size / union.size;
  }
}
